import { useCallback, useEffect, useState } from 'react';
import { IS_BROWSER } from '@utils/constants/app-constants.ts';

export const SIDEBAR_COLLAPSED_STORAGE_KEY =
  'scriptix_editor_sidebar_collapsed';
export const DEFAULT_SIDEBAR_COLLAPSED = true;

/**
 * Parse a persisted sidebar-collapsed value. Only the literal strings
 * `'true'` / `'false'` are honoured; anything else (missing key, empty
 * string, JSON blob, legacy typo) falls back to the collapsed default so
 * the editor rail never boots into an unexpected state.
 */
export function parseSidebarCollapsed(raw: string | null): boolean {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return DEFAULT_SIDEBAR_COLLAPSED;
}

/** Read the current persisted state from a Storage-like object. */
export function readSidebarCollapsed(
  storage: Pick<Storage, 'getItem'> | null | undefined
): boolean {
  if (!storage) return DEFAULT_SIDEBAR_COLLAPSED;
  try {
    return parseSidebarCollapsed(
      storage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)
    );
  } catch {
    return DEFAULT_SIDEBAR_COLLAPSED;
  }
}

/** Write the new state to a Storage-like object. Errors are swallowed. */
export function writeSidebarCollapsed(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  next: boolean
): void {
  if (!storage) return;
  try {
    storage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
  } catch {
    /* noop */
  }
}

/**
 * Editor sidebar rail state (#2230). Persisted per browser and shared
 * across the transcript and caption editors via a single localStorage
 * key + cross-tab `storage` sync.
 */
export function useSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readSidebarCollapsed(IS_BROWSER ? window.localStorage : null)
  );

  const set = useCallback((next: boolean) => {
    setCollapsed(next);
    if (IS_BROWSER) writeSidebarCollapsed(window.localStorage, next);
  }, []);

  useEffect(() => {
    if (!IS_BROWSER) return;
    const handler = (e: StorageEvent) => {
      if (e.key !== SIDEBAR_COLLAPSED_STORAGE_KEY) return;
      if (e.newValue === 'true') setCollapsed(true);
      else if (e.newValue === 'false') setCollapsed(false);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  return [collapsed, set];
}
